import { useMemo, useState } from 'react';
import { doc } from 'firebase/firestore';
import { db } from '../../lib/firestore';
import { getChecklist } from '../../lib/checklists';
import { NIDATA_LOADING_LABEL } from '../../lib/nidataUiCopy';
import {
  SIREN_HN_COUNT,
  emptySirenAnswers,
  isSirenBundleDepartmentExcluded,
  readSirenAnswers,
  readSirenHn,
  sirenGridIsComplete,
} from '../../lib/sirenBundlePolicy';
import { getCurrentMonthKey } from '../ComplianceHub.helpers';
import { normalizeSubmissionShape } from '../../services/submissionShape';
import {
  clearDepartmentDraft,
  readDraftFromLocalStorage,
  submissionDraftMatchesScope,
  writeDraftToLocalStorage,
  type SubmissionDocument,
  type SubmissionMetadata,
} from '../../services/submissions';
import { getReportingMonth } from '../../lib/reportingMonth';
import {
  formatWardSubmitClosedMessage,
  isWardSubmitRetired,
  resolveWardSubmitDisplayTitle,
  wardSubmitSubmitClosedMessage,
} from '../../lib/wardSubmitRetired';

const SIREN_TITLE = 'SIREN bundle';
const SIREN_DOC_ID = 'siren_bundle';

type GridRow = {
  section: string;
  label: string;
  itemIndex: number;
  showSection: boolean;
  sectionSpan: number;
};

function buildRows(): GridRow[] {
  const rows: GridRow[] = [];
  let itemIndex = 0;
  for (const section of getChecklist(SIREN_TITLE)) {
    const sectionTitle = section.title ?? '';
    section.items.forEach((label, index) => {
      rows.push({
        section: sectionTitle,
        label,
        itemIndex,
        showSection: index === 0,
        sectionSpan: section.items.length,
      });
      itemIndex += 1;
    });
  }
  return rows;
}

function loadSirenDraft(monthKey: string, legacyMonthKey: string, departmentId: string | null) {
  const raw = readDraftFromLocalStorage(monthKey) ?? readDraftFromLocalStorage(legacyMonthKey);
  if (!raw || !submissionDraftMatchesScope(raw, departmentId)) {
    return {
      hn: Array.from({ length: SIREN_HN_COUNT }, () => ''),
      answers: emptySirenAnswers(),
    };
  }
  const nested =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? (raw.data as SubmissionDocument)
      : null;
  const shape = normalizeSubmissionShape(raw as Record<string, unknown>);
    return {
      hn: readSirenHn(raw.hn ?? nested?.hn),
      answers: readSirenAnswers(shape.answers),
    };
}

function ColumnFillButtons({
  hnNumber,
  disabled,
  onFill,
}: {
  hnNumber: number;
  disabled: boolean;
  onFill: (value: string) => void;
}) {
  const buttons = [
    { value: '1', label: 'ใส่ 1 ทุกข้อ', background: '#22C55E', textClass: 'text-white' },
    { value: '0', label: 'ใส่ 0 ทุกข้อ', background: '#EF4444', textClass: 'text-white' },
    { value: 'NA', label: 'ใส่ NA ทุกข้อ', background: '#94A3B8', textClass: 'text-slate-800' },
  ];
  return (
    <div className="mb-2 flex flex-col gap-1" role="group" aria-label={`ใส่คำตอบทุกข้อของ HN ${hnNumber}`}>
      {buttons.map((button) => (
        <button
          key={button.value}
          type="button"
          disabled={disabled}
          onClick={() => onFill(button.value)}
          className={`min-h-[44px] cursor-pointer rounded-full px-2 text-xs font-medium ${button.textClass} disabled:cursor-not-allowed disabled:opacity-50`}
          style={{ background: button.background }}
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

function ChoiceTrio({
  selected,
  disabled,
  onSelect,
}: {
  selected: string;
  disabled: boolean;
  onSelect: (value: string) => void;
}) {
  const options = [
    { value: '1', color: '#22C55E' },
    { value: '0', color: '#EF4444' },
    { value: 'NA', color: '#94A3B8' },
  ];
  return (
    <div className="inline-flex rounded-full border border-slate-200" role="group" aria-label="ตัวเลือก 1/0/NA">
      {options.map((option) => {
        const active = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onSelect(option.value)}
            className={`min-h-[44px] min-w-[44px] px-2 text-sm font-medium ${active ? 'text-white' : 'text-slate-700'}`}
            style={{ background: active ? option.color : 'transparent' }}
          >
            {option.value}
          </button>
        );
      })}
    </div>
  );
}

export type SirenBundleFormProps = {
  departmentId: string | null;
  reportingMonthDate: Date;
  currentUserId: string | null;
  submissionMetadata: SubmissionMetadata;
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
};

export function SirenBundleForm({
  departmentId,
  reportingMonthDate,
  currentUserId,
  submissionMetadata,
  readOnly,
  onClose,
  onSaved,
}: SirenBundleFormProps) {
  const rows = useMemo(() => buildRows(), []);
  const reportingMonth = useMemo(() => getReportingMonth(reportingMonthDate), [reportingMonthDate]);
  const monthKey = getCurrentMonthKey(SIREN_DOC_ID, reportingMonthDate, departmentId, currentUserId);
  const legacyMonthKey = getCurrentMonthKey(SIREN_TITLE, reportingMonthDate, departmentId, currentUserId);
  const initial = useMemo(
    () => loadSirenDraft(monthKey, legacyMonthKey, departmentId),
    [departmentId, legacyMonthKey, monthKey],
  );
  const [hn, setHn] = useState(initial.hn);
  const [answers, setAnswers] = useState(initial.answers);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setAnswer = (hnIndex: number, itemIndex: number, value: string) => {
    setAnswers((current) =>
      current.map((row, index) => (index === hnIndex ? row.map((cell, cellIndex) => (cellIndex === itemIndex ? value : cell)) : row)),
    );
  };

  const fillColumn = (hnIndex: number, value: string) => {
    if (readOnly) return;
    setAnswers((current) =>
      current.map((row, index) => (index === hnIndex ? row.map(() => value) : row)),
    );
  };

  const handleSubmit = async () => {
    if (readOnly || saving) return;
    if (isWardSubmitRetired({ categoryDocId: SIREN_DOC_ID, title: SIREN_TITLE })) {
      const displayTitle =
        resolveWardSubmitDisplayTitle({ categoryDocId: SIREN_DOC_ID, title: SIREN_TITLE }) ?? SIREN_TITLE;
      setError(formatWardSubmitClosedMessage(wardSubmitSubmitClosedMessage(displayTitle)));
      return;
    }
    if (!departmentId) {
      setError('กรุณาเลือกแผนกก่อนส่งข้อมูล');
      return;
    }
    if (isSirenBundleDepartmentExcluded(departmentId)) {
      setError('หน่วยงานนี้ไม่ต้องส่ง SIREN bundle');
      return;
    }
    if (!sirenGridIsComplete({ hn, answers })) {
      setError('กรอก HN ให้ครบ 5 ช่อง และเลือกคำตอบทุกช่องก่อนส่ง');
      return;
    }
    const snapshot: SubmissionDocument = {
      answers,
      hn,
      submitted: Array.from({ length: SIREN_HN_COUNT }, () => true),
      incomplete: Array.from({ length: SIREN_HN_COUNT }, () => false),
      chartTitles: Array.from({ length: SIREN_HN_COUNT }, (_, index) => `HN ${index + 1}`),
      categoryId: SIREN_DOC_ID,
      categoryTitle: SIREN_TITLE,
      categoryDocId: SIREN_DOC_ID,
    };
    setSaving(true);
    setError(null);
    try {
      await clearDepartmentDraft(departmentId, reportingMonth, SIREN_DOC_ID, snapshot, {
        ...submissionMetadata,
        departmentId,
        departmentRef: doc(db, 'departments', departmentId),
        submittedAt: new Date(),
        updatedAt: new Date(),
        status: 'submitted',
      });
      writeDraftToLocalStorage(monthKey, snapshot, false);
      onSaved();
      onClose();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'ส่งข้อมูลไม่สำเร็จ';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-6xl flex-col bg-white shadow-xl" aria-label="SIREN bundle">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <h2 className="text-lg font-semibold text-slate-900">SIREN bundle</h2>
        <button type="button" onClick={onClose} className="min-h-[44px] rounded-full px-4 text-sm text-slate-700">
          ปิด
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        <p className="mb-3 text-sm text-slate-600">ใส่ 1 เมื่อทำแล้ว ใส่ 0 เมื่อไม่ได้ทำ ใส่ NA เมื่อไม่ต้องทำในข้อนั้น</p>
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
        <div className="overflow-x-auto">
          <table className="min-w-[960px] border-collapse text-sm text-slate-800">
            <thead>
              <tr>
                <th colSpan={2} className="border border-slate-300 bg-slate-50 px-3 py-2 text-left">
                  หัวข้อประเมิน
                </th>
                {hn.map((value, hnIndex) => (
                  <th key={`hn-head-${hnIndex}`} className="border border-slate-300 bg-slate-50 px-2 py-2 align-top">
                    <ColumnFillButtons
                      hnNumber={hnIndex + 1}
                      disabled={readOnly}
                      onFill={(value) => fillColumn(hnIndex, value)}
                    />
                    <label className="flex flex-col gap-1 text-left font-medium">
                      HN:
                      <input
                        value={value}
                        disabled={readOnly}
                        onChange={(event) =>
                          setHn((current) => current.map((cell, index) => (index === hnIndex ? event.target.value : cell)))
                        }
                        className="h-11 min-h-[44px] rounded-lg border border-slate-300 px-2 font-normal"
                        aria-label={`HN ${hnIndex + 1}`}
                      />
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`item-${row.itemIndex}`}>
                  {row.showSection ? (
                    <th
                      rowSpan={row.sectionSpan}
                      className="w-28 border border-slate-300 bg-slate-50 px-2 py-2 text-left align-top font-semibold"
                    >
                      {row.section}
                    </th>
                  ) : null}
                  <td className="border border-slate-300 px-3 py-2 align-top whitespace-pre-line">{row.label}</td>
                  {answers.map((answerRow, hnIndex) => (
                    <td key={`item-${row.itemIndex}-${hnIndex}`} className="border border-slate-300 px-2 py-2 align-top">
                      <ChoiceTrio
                        selected={answerRow[row.itemIndex] ?? ''}
                        disabled={readOnly}
                        onSelect={(value) => setAnswer(hnIndex, row.itemIndex, value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <footer className="flex justify-end border-t border-slate-200 px-4 py-3">
        <button
          type="button"
          disabled={readOnly || saving}
          onClick={() => {
            void handleSubmit();
          }}
          className="min-h-[44px] rounded-full bg-slate-900 px-5 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? NIDATA_LOADING_LABEL : 'ส่งแบบประเมิน'}
        </button>
      </footer>
    </aside>
  );
}
